'use client';

import { cn } from '@/lib/utils';

type VisualizationLoaderKind = 'generic' | 'html' | 'svg' | '3d' | 'animation';

function resolveTitle(kind: VisualizationLoaderKind, title?: string) {
  if (title && title.trim()) {
    return title.trim();
  }

  switch (kind) {
    case 'html':
      return 'Building HTML visualization';
    case 'svg':
      return 'Drawing SVG visualization';
    case '3d':
      return 'Preparing 3D visualization';
    case 'animation':
      return 'Preparing interactive animation';
    default:
      return 'Preparing visualization';
  }
}

function resolveSubtitle(kind: VisualizationLoaderKind) {
  switch (kind) {
    case 'html':
      return 'Laying out panels, controls, and content blocks';
    case 'svg':
      return 'Positioning shapes, labels, and precise vector paths';
    case '3d':
      return 'Compiling scene geometry, lighting, and camera setup';
    case 'animation':
      return 'Assembling frames, motion, and runtime hooks';
    default:
      return 'Loading tool output';
  }
}

function SkeletonBar({ className }: { className?: string }) {
  return <div className={cn('rounded-full bg-white/12 animate-pulse', className)} />;
}

function HtmlLoaderPreview() {
  return (
    <div className="grid h-full gap-4 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
        <div className="mb-4 flex items-center gap-2">
          <div className="size-2.5 rounded-full bg-rose-300/70 animate-pulse" />
          <div className="size-2.5 rounded-full bg-amber-200/80 animate-pulse [animation-delay:120ms]" />
          <div className="size-2.5 rounded-full bg-emerald-300/70 animate-pulse [animation-delay:240ms]" />
          <div className="ml-3 h-2.5 w-24 rounded-full bg-white/10 animate-pulse" />
        </div>
        <div className="space-y-3">
          <SkeletonBar className="h-3 w-36" />
          <SkeletonBar className="h-10 w-full rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
              <SkeletonBar className="h-2.5 w-16" />
              <SkeletonBar className="mt-3 h-14 w-full rounded-lg" />
              <SkeletonBar className="mt-3 h-2.5 w-20" />
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
              <SkeletonBar className="h-2.5 w-20" />
              <SkeletonBar className="mt-3 h-6 w-full rounded-lg" />
              <SkeletonBar className="mt-2 h-6 w-4/5 rounded-lg" />
              <SkeletonBar className="mt-2 h-6 w-3/5 rounded-lg" />
            </div>
          </div>
        </div>
      </div>
      <div className="grid gap-3">
        <div className="rounded-2xl border border-primary/15 bg-primary/[0.06] p-4">
          <SkeletonBar className="h-2.5 w-28 bg-primary/30" />
          <SkeletonBar className="mt-3 h-24 w-full rounded-2xl bg-primary/18" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="rounded-xl border border-white/8 bg-white/[0.03] p-3"
            >
              <SkeletonBar className="h-10 w-full rounded-lg" />
              <SkeletonBar className="mt-3 h-2 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SvgLoaderPreview() {
  return (
    <div className="relative h-full overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))]">
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      <div className="absolute left-8 top-8 h-16 w-16 rounded-full border border-sky-300/40 bg-sky-400/15 animate-pulse" />
      <div className="absolute left-24 top-16 h-px w-28 bg-sky-300/45" />
      <div className="absolute left-48 top-12 h-12 w-20 rounded-2xl border border-fuchsia-300/40 bg-fuchsia-400/10 animate-pulse [animation-delay:160ms]" />
      <div className="absolute left-56 top-24 h-px w-32 bg-fuchsia-300/40" />
      <div className="absolute right-12 top-12 h-24 w-24 rounded-[1.75rem] border border-emerald-300/35 bg-emerald-300/10 animate-pulse [animation-delay:280ms]" />
      <div className="absolute bottom-12 left-12 right-12 h-20 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
        <SkeletonBar className="h-2.5 w-28" />
        <div className="mt-4 flex items-end gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary/20 animate-pulse" />
          <div className="h-14 w-8 rounded-lg bg-cyan-400/20 animate-pulse [animation-delay:120ms]" />
          <div className="h-20 w-8 rounded-lg bg-fuchsia-400/20 animate-pulse [animation-delay:240ms]" />
          <div className="h-10 w-8 rounded-lg bg-emerald-400/20 animate-pulse [animation-delay:360ms]" />
          <div className="h-16 w-8 rounded-lg bg-amber-300/20 animate-pulse [animation-delay:480ms]" />
        </div>
      </div>
    </div>
  );
}

function ThreeDLoaderPreview() {
  return (
    <div className="relative h-full overflow-hidden rounded-2xl border border-white/10 bg-[radial-gradient(circle_at_top,rgba(74,108,247,0.22),transparent_34%),linear-gradient(180deg,#050816,#09111f_62%,#050816)]">
      <div className="absolute inset-x-10 bottom-8 h-12 rounded-[999px] border border-white/10 bg-white/[0.02]" />
      <div className="absolute left-1/2 top-[52%] h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-300/30 animate-[spin_12s_linear_infinite]" />
      <div className="absolute left-1/2 top-[52%] h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-[32%] bg-[linear-gradient(135deg,rgba(34,211,238,0.32),rgba(168,85,247,0.35))] blur-[1px] animate-[float_4s_ease-in-out_infinite]" />
      <div className="absolute left-1/2 top-[52%] h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10" />
      <div className="absolute left-6 top-6 space-y-2 rounded-2xl border border-white/8 bg-black/20 p-3 backdrop-blur-sm">
        <SkeletonBar className="h-2.5 w-20" />
        <SkeletonBar className="h-2 w-28 bg-cyan-300/20" />
        <SkeletonBar className="h-2 w-24 bg-fuchsia-300/20" />
      </div>
      <div className="absolute right-6 top-8 w-28 rounded-2xl border border-white/8 bg-black/20 p-3 backdrop-blur-sm">
        <SkeletonBar className="h-2.5 w-16" />
        <SkeletonBar className="mt-3 h-8 w-full rounded-xl bg-white/10" />
        <SkeletonBar className="mt-2 h-2 w-3/4 bg-white/12" />
      </div>
    </div>
  );
}

function AnimationLoaderPreview() {
  return (
    <div className="grid h-full gap-4 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
            <div className="ml-0.5 h-0 w-0 border-y-[7px] border-y-transparent border-l-[11px] border-l-primary animate-pulse" />
          </div>
          <div className="flex-1">
            <SkeletonBar className="h-2.5 w-28" />
            <SkeletonBar className="mt-2 h-2 w-40 bg-primary/20" />
          </div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-slate-950/60 p-4">
          <SkeletonBar className="h-32 w-full rounded-2xl bg-gradient-to-r from-primary/15 via-fuchsia-400/20 to-cyan-400/15" />
          <div className="mt-4 flex items-center gap-2">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="h-2 flex-1 rounded-full bg-white/10 animate-pulse"
                style={{ animationDelay: `${index * 120}ms` }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
            <SkeletonBar className="h-2.5 w-24" />
            <SkeletonBar className="mt-3 h-10 w-full rounded-xl" />
            <SkeletonBar className="mt-3 h-2 w-4/5" />
          </div>
        ))}
      </div>
    </div>
  );
}

function renderPreview(kind: VisualizationLoaderKind) {
  switch (kind) {
    case 'html':
      return <HtmlLoaderPreview />;
    case 'svg':
      return <SvgLoaderPreview />;
    case '3d':
      return <ThreeDLoaderPreview />;
    case 'animation':
      return <AnimationLoaderPreview />;
    default:
      return (
        <div className="flex h-full items-center justify-center rounded-2xl border border-white/8 bg-white/[0.03]">
          <div className="flex items-center gap-3 text-sm text-white/70">
            <div className="animate-spin-ease">
              <div className="size-5 rounded-full border border-primary/40 border-t-primary" />
            </div>
            <span>Loading tool output</span>
          </div>
        </div>
      );
  }
}

export function VisualizationLoader({
  kind,
  title,
  className,
}: {
  kind: VisualizationLoaderKind;
  title?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'my-2 overflow-hidden rounded-2xl border border-border/60 bg-[linear-gradient(180deg,rgba(9,14,28,0.98),rgba(12,18,33,0.98))] p-4 text-white shadow-sm',
        'h-[clamp(220px,28vh,320px)]',
        className,
      )}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.24em] text-white/45">
            Preparing visual
          </p>
          <p className="mt-1 truncate text-sm font-semibold text-white/92">
            {resolveTitle(kind, title)}
          </p>
          <p className="mt-1 text-xs text-white/55">
            {resolveSubtitle(kind)}
          </p>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-white/55">
          <span className="size-2 rounded-full bg-primary animate-pulse" />
          Loading
        </div>
      </div>

      <div className="h-[calc(100%-72px)]">
        {renderPreview(kind)}
      </div>
    </div>
  );
}
